import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Locale = 'en' | 'es';

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  availableLocales: { code: Locale; name: string }[];
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const [locale, setLocaleState] = useState<Locale>(() => {
    // Check localStorage for saved locale preference
    const savedLocale = localStorage.getItem('loglimit-language') as Locale;
    if (savedLocale && ['en', 'es'].includes(savedLocale)) {
      return savedLocale;
    }

    // Check browser language
    const browserLang = navigator.language.split('-')[0];
    if (['en', 'es'].includes(browserLang)) {
      return browserLang as Locale;
    }

    return 'en';
  });

  const availableLocales = [
    { code: 'en' as Locale, name: 'English' },
    { code: 'es' as Locale, name: 'Español' },
  ];

  useEffect(() => {
    i18n.changeLanguage(locale);
    localStorage.setItem('loglimit-language', locale);
  }, [locale, i18n]);

  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
  };

  return (
    <LocaleContext.Provider value={{ locale, setLocale, availableLocales }}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};
